import { LitElement, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhX";
import "@phosphor-icons/webcomponents/PhCalendar";
import "@phosphor-icons/webcomponents/PhClock";
import "@phosphor-icons/webcomponents/PhStop";
import "@phosphor-icons/webcomponents/PhWarning";
import "@phosphor-icons/webcomponents/PhCopy";
import "@phosphor-icons/webcomponents/PhBroadcast";
import "ui-components/button/wow-button";
import "ui-components/form-elements/input/wow-input";
import "ui-components/form-elements/textarea/textarea";
import "ui-components/form-elements/select/wow-select";
import "ui-components/media-uploader/media-uploader";
import "ui-components/modal/wow-modal";
import "ui-components/confirmation/wow-confirmation";
import { NotificationService } from "ui-components/notification";
// TODO: stream-stats was built for SRS, needs reimplementation for current streaming
// import "../stream-stats/stream-stats";
import "../stream-config-modal/stream-config-modal";
import "../live-stream-modal/live-stream-modal";

import { styles } from "./edit-live-event-modal.css";
import { rpc } from "ui-common/services/rpc";
import { t, translate } from "../../translations/i18n";

const MAX_ATTENDEES_LIMIT = 10000;
const MAX_PRICE_LIMIT = 999;

@customElement("edit-live-event-modal")
export class EditLiveEventModal extends LitElement {
  static override styles = [styles];

  @property({ type: String })
  eventId!: string;

  @property({ type: Array })
  featureFlags: string[] = [];

  @state()
  private eventTitle: string = "";

  @state()
  private description = "";

  @state()
  private contentTier: "free" | "premium" = "premium";

  @state()
  private feeEuros = "";

  @state()
  private scheduledDate = "";

  @state()
  private scheduledTime = "";

  @state()
  private maxAttendees = "";

  @state()
  private thumbnailFileId?: string;

  @state()
  private thumbnailUrl?: string;

  @state()
  private whatsappUrl = "";

  @state()
  private postBuyingVideoUrl = "";

  @state()
  private postBuyingVideoFileId?: string;

  @state()
  private ctaText = "";

  @state()
  private backgroundImageFileIds: string[] = [];

  @state()
  private backgroundImageUrls: string[] = [];

  @state()
  private eventStatus: string = "";

  @state()
  private isSubmitting = false;

  @state()
  private isEnding = false;

  @state()
  private errors: Record<string, string> = {};

  @state()
  private isLoadingEvent = false;

  @state()
  private showEndConfirmation = false;

  @state()
  private showStreamConfigModal = false;

  @state()
  private showLiveStreamModal = false;

  @state()
  private streamConfig: { aspectRatio: "landscape" | "portrait"; enableAudio: boolean } | null = null;

  private readonly RTMP_URL = "rtmp://stream.worldofwow.com/streams";

  override connectedCallback() {
    super.connectedCallback();
    this.loadEventData();
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
  }

  private async loadEventData() {
    if (!this.eventId) return;

    try {
      this.isLoadingEvent = true;

      const result = await rpc("get-live-event-by-id", { event_id: this.eventId });

      if (!result.event) {
        NotificationService.error(translate("editLiveEventModal.eventNotFound"));
        return;
      }

      const event = result.event;

      // Pre-fill form fields
      this.eventTitle = event.title || "";
      this.description = event.description || "";
      this.contentTier = event.content_tier === "premium" ? "premium" : "free";
      this.feeEuros = event.fee_cents ? (event.fee_cents / 100).toFixed(2) : "";
      this.maxAttendees = event.max_attendees ? event.max_attendees.toString() : "";
      this.thumbnailFileId = event.thumbnail_file_id || undefined;
      this.thumbnailUrl = event.thumbnail_url || undefined;
      this.whatsappUrl = event.whatsapp_url || "";
      this.postBuyingVideoFileId = event.post_buying_video_file_id || undefined;
      this.postBuyingVideoUrl = event.post_buying_video_url || "";
      this.ctaText = event.cta_text || "";
      this.backgroundImageFileIds = event.background_image_file_ids || [];
      this.backgroundImageUrls = event.background_image_urls || [];
      this.eventStatus = event.status || "";

      // Parse date and time from ISO string (use local time consistently)
      if (event.scheduled_start_time) {
        const date = new Date(event.scheduled_start_time);
        // Format as YYYY-MM-DD for date input (local time)
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        this.scheduledDate = `${year}-${month}-${day}`;
        // Format as HH:mm for time input (local time)
        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");
        this.scheduledTime = `${hours}:${minutes}`;
      }
    } catch (error) {
      console.error("Error loading event:", error);
      NotificationService.error(translate("editLiveEventModal.failedToLoad"));
    } finally {
      this.isLoadingEvent = false;
    }
  }

  private validateForm(): boolean {
    const newErrors: Record<string, string> = {};

    // Check if event has started (scheduled time has passed)
    const hasEventStarted =
      this.scheduledDate && this.scheduledTime
        ? new Date(`${this.scheduledDate}T${this.scheduledTime}`).getTime() <= Date.now()
        : false;

    // Don't validate form if event is read-only (live or started)
    if (this.eventStatus === "live" || hasEventStarted) {
      return true;
    }

    if (!this.eventTitle || !this.eventTitle.trim()) {
      newErrors.title = translate("editLiveEventModal.titleRequired");
    }

    if (!this.description || !this.description.trim()) {
      newErrors.description = translate("editLiveEventModal.descriptionRequired");
    }

    if (!this.scheduledDate) {
      newErrors.date = translate("editLiveEventModal.dateRequired");
    }

    if (!this.scheduledTime) {
      newErrors.time = translate("editLiveEventModal.timeRequired");
    }

    if (this.scheduledDate && this.scheduledTime) {
      const scheduledDateTime = new Date(`${this.scheduledDate}T${this.scheduledTime}`);
      if (scheduledDateTime <= new Date()) {
        newErrors.datetime = translate("editLiveEventModal.dateMustBeFuture");
      }
    }

    if (this.maxAttendees && this.maxAttendees.trim() !== "") {
      const numValue = Number(this.maxAttendees);
      if (isNaN(numValue) || numValue < 1) {
        newErrors.maxAttendees = translate("editLiveEventModal.maxAttendeesMustBePositive");
      }

      if (numValue > MAX_ATTENDEES_LIMIT) {
        newErrors.maxAttendees = translate("editLiveEventModal.maxAttendeesLimit");
      }
    }

    if (this.contentTier === "premium") {
      if (!this.feeEuros || !this.feeEuros.trim()) {
        newErrors.fee = translate("editLiveEventModal.priceRequired");
      } else {
        const numValue = parseFloat(this.feeEuros);
        if (isNaN(numValue) || numValue <= 0) {
          newErrors.fee = translate("editLiveEventModal.pricePositive");
        } else if (numValue > MAX_PRICE_LIMIT) {
          newErrors.fee = translate("editLiveEventModal.priceLimit");
        }
      }
    }

    this.errors = newErrors;
    return Object.keys(newErrors).length === 0;
  }

  private async handleSubmit() {
    if (!this.validateForm()) {
      return;
    }

    this.isSubmitting = true;

    try {
      // Parse as local time to validate
      const scheduledDateTime = new Date(`${this.scheduledDate}T${this.scheduledTime}`);

      if (isNaN(scheduledDateTime.getTime())) {
        NotificationService.error(translate("editLiveEventModal.invalidDateTime"));
        this.isSubmitting = false;
        return;
      }

      if (scheduledDateTime <= new Date()) {
        NotificationService.error(translate("editLiveEventModal.dateMustBeFuture"));
        this.isSubmitting = false;
        return;
      }

      const localDateTime = new Date(`${this.scheduledDate}T${this.scheduledTime}:00`);
      const isoString = localDateTime.toISOString();

      const updateParams = {
        event_id: this.eventId,
        title: (this.eventTitle || "").trim(),
        description: (this.description || "").trim(),
        content_tier: this.contentTier,
        fee_cents: this.contentTier === "premium" && this.feeEuros ? Math.round(parseFloat(this.feeEuros) * 100) : null,
        scheduled_start_time: isoString,
        max_attendees: this.maxAttendees ? Number(this.maxAttendees) : null,
        thumbnail_file_id: this.thumbnailFileId || null,
        whatsapp_url: this.whatsappUrl?.trim() || null,
        post_buying_video_file_id: this.postBuyingVideoFileId || null,
        cta_text: this.ctaText?.trim() || null,
        background_image_file_ids: this.backgroundImageFileIds.length > 0 ? this.backgroundImageFileIds : null,
      };

      const result = await rpc("update-live-event", updateParams);

      if (result && typeof result === "object" && "success" in result && result.success) {
        NotificationService.success(translate("editLiveEventModal.updateSuccess"));

        // Dispatch event to refresh the list
        this.dispatchEvent(
          new CustomEvent("event-updated", {
            detail: { event: (result as { event?: unknown }).event },
            bubbles: true,
          }),
        );

        // Close modal after a short delay
        setTimeout(() => {
          this.handleClose();
        }, 500);
      } else {
        const message =
          result && typeof result === "object" && "message" in result
            ? (result as { message?: string }).message || translate("editLiveEventModal.failedToUpdate")
            : translate("editLiveEventModal.failedToUpdate");
        NotificationService.error(message);
      }
    } catch {
      NotificationService.error(translate("editLiveEventModal.failedToUpdate"));
    } finally {
      this.isSubmitting = false;
    }
  }

  private handleEndEventClick() {
    this.showEndConfirmation = true;
  }

  private handleCancelEnd() {
    this.showEndConfirmation = false;
  }

  private async handleConfirmEnd() {
    try {
      this.isEnding = true;
      this.showEndConfirmation = false;
      const result = await rpc("end-live-event", { event_id: this.eventId });

      if (result.success) {
        NotificationService.success(translate("editLiveEventModal.endEventSuccess"));

        this.dispatchEvent(
          new CustomEvent("event-ended", {
            detail: { eventId: this.eventId },
            bubbles: true,
            composed: true,
          }),
        );

        setTimeout(() => {
          this.handleClose();
        }, 1000);
      } else {
        NotificationService.error(translate("editLiveEventModal.endEventFailed"));
      }
    } catch (error) {
      console.error("Error ending event:", error);
      NotificationService.error(translate("editLiveEventModal.endEventFailed"));
    } finally {
      this.isEnding = false;
    }
  }

  private handleClose() {
    this.dispatchEvent(new CustomEvent("modal-close", { bubbles: true }));
  }

  private handleThumbnailUpload(e: CustomEvent) {
    const { fileId } = e.detail;
    this.thumbnailFileId = fileId;
  }

  private handleVideoUpload(e: CustomEvent) {
    const { fileId } = e.detail;
    this.postBuyingVideoFileId = fileId;
  }

  private handleBackgroundImageUpload(index: number, e: CustomEvent) {
    const { fileId, fileUrl } = e.detail;
    const newFileIds = [...this.backgroundImageFileIds];
    const newUrls = [...this.backgroundImageUrls];

    if (fileId) {
      newFileIds[index] = fileId;
      newUrls[index] = fileUrl || "";
    } else {
      newFileIds.splice(index, 1);
      newUrls.splice(index, 1);
    }

    this.backgroundImageFileIds = newFileIds.filter(Boolean);
    this.backgroundImageUrls = newUrls.filter(Boolean);
  }

  private handleFeeInput(e: Event) {
    const input = e.target as HTMLInputElement;
    let value = input.value;

    // Allow only numbers and dots (no commas)
    value = value.replace(/[^\d.]/g, "");

    // Allow only one dot
    const dotIndex = value.indexOf(".");
    if (dotIndex !== -1) {
      const beforeDot = value.substring(0, dotIndex);
      const afterDot = value.substring(dotIndex + 1).replace(/\./g, "");
      // Limit to 2 decimal places
      value = beforeDot + "." + afterDot.substring(0, 2);
    }

    this.feeEuros = value;
    input.value = value;
    this.errors = { ...this.errors, fee: "" };
  }

  private get hasBothLiveEventFlags(): boolean {
    return this.featureFlags.includes("open-live-events") && this.featureFlags.includes("paid-live-events");
  }

  private get todayDate(): string {
    return new Date().toISOString().split("T")[0]!;
  }

  private get hasBackgroundImagesFlag(): boolean {
    return this.featureFlags.includes("live-event-background-images");
  }

  private handleStartStreamingClick(): void {
    this.showStreamConfigModal = true;
  }

  private handleStreamConfigCancel(): void {
    this.showStreamConfigModal = false;
  }

  private handleStartStream(e: CustomEvent): void {
    this.streamConfig = e.detail;
    this.showStreamConfigModal = false;
    this.showLiveStreamModal = true;
  }

  private handleStreamEnded(): void {
    this.showLiveStreamModal = false;
    this.streamConfig = null;
  }

  private async copyToClipboard(text: string, label: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(text);
      NotificationService.success(`${label} copied to clipboard`);
    } catch {
      NotificationService.error(`Failed to copy ${label}`);
    }
  }

  protected override render() {
    // Check if scheduled time has passed
    const hasScheduledTimePassed =
      this.scheduledDate && this.scheduledTime
        ? new Date(`${this.scheduledDate}T${this.scheduledTime}`).getTime() <= Date.now()
        : false;

    const isLive = this.eventStatus === "live";
    const isEnded = this.eventStatus === "ended";

    // Show end button if event is actually live OR if scheduled time has passed (so creator can mark it as ended)
    const canEndEvent = isLive || (this.eventStatus === "scheduled" && hasScheduledTimePassed);

    return html`
      <wow-modal
        mobile-fullscreen
        ?disable-primary-button=${this.isSubmitting || isEnded || this.isLoadingEvent}
        @close=${this.handleClose}
        @footer-button-clicked=${this.handleSubmit}
      >
        <span slot="header">
          ${this.isLoadingEvent
            ? t("editLiveEventModal.headerLoading")
            : isEnded
              ? t("editLiveEventModal.headerEnded")
              : isLive
                ? t("editLiveEventModal.headerLive")
                : t("editLiveEventModal.headerEdit")}
        </span>

        <div class="form-content">
          ${this.isLoadingEvent
            ? html`<div style="text-align: center; padding: 40px;">${t("editLiveEventModal.loadingEventData")}</div>`
            : html`
                <!-- Event Cover Image -->
                <media-uploader
                  responsive
                  context="cover_image"
                  label="${t("editLiveEventModal.firstThumb")}"
                  viewport="cover"
                  currentFileUrl=${this.thumbnailUrl}
                  @change=${this.handleThumbnailUpload}
                ></media-uploader>

                <!-- Title -->
                <wow-input
                  label="${t("editLiveEventModal.title")}"
                  placeholder="${t("editLiveEventModal.titlePlaceholder")}"
                  .value=${this.eventTitle || ""}
                  @input=${(e: Event) => {
                    this.eventTitle = (e.target as HTMLInputElement).value || "";
                    this.errors = { ...this.errors, title: "" };
                  }}
                  ?error=${!!this.errors.title}
                  help-text=${this.errors.title || ""}
                  required
                ></wow-input>

                <!-- Description -->
                <wow-textarea
                  label="${t("editLiveEventModal.description")}"
                  placeholder="${t("editLiveEventModal.descriptionPlaceholder")}"
                  .value=${this.description || ""}
                  @input=${(e: Event) => {
                    this.description = (e.target as HTMLTextAreaElement).value || "";
                    this.errors = { ...this.errors, description: "" };
                  }}
                  ?error=${!!this.errors.description}
                  help-text=${this.errors.description || ""}
                  rows="3"
                  maxlength="750"
                  required
                ></wow-textarea>

                ${this.hasBothLiveEventFlags
                  ? html`
                      <wow-select
                        label="${t("editLiveEventModal.accessLevel")}"
                        .options=${[
                          { value: "free", label: translate("editLiveEventModal.accessLevelFree") },
                          { value: "premium", label: translate("editLiveEventModal.accessLevelPremium") },
                        ]}
                        .value=${this.contentTier}
                        @change=${(e: CustomEvent) => {
                          this.contentTier = e.detail.value;
                          this.errors = { ...this.errors, fee: "" };
                        }}
                      ></wow-select>
                    `
                  : nothing}
                ${this.contentTier === "premium"
                  ? html`
                      <wow-input
                        type="number"
                        inputmode="decimal"
                        label="${t("createLiveEventsModal.price")}"
                        placeholder="${t("createLiveEventsModal.pricePlaceholder")}"
                        help-text=${this.errors.fee || t("createLiveEventsModal.priceHelper")}
                        min="0.50"
                        max="${MAX_PRICE_LIMIT}"
                        step="0.01"
                        pattern="^\\d+(\\.\\d{1,2})?$"
                        .value=${this.feeEuros}
                        @input=${this.handleFeeInput}
                        ?error=${!!this.errors.fee}
                        required
                      ></wow-input>
                    `
                  : nothing}

                <!-- Date and Time -->
                <div class="form-row">
                  <wow-input
                    type="date"
                    label="${t("editLiveEventModal.date")}"
                    .value=${this.scheduledDate}
                    min=${this.todayDate}
                    @input=${(e: Event) => {
                      this.scheduledDate = (e.target as HTMLInputElement).value;
                      this.errors = { ...this.errors, date: "", datetime: "" };
                    }}
                    ?error=${!!this.errors.date}
                    help-text=${this.errors.date || (isEnded ? translate("editLiveEventModal.dateCannotChange") : "")}
                    ?disabled=${isEnded}
                    required
                  ></wow-input>

                  <wow-input
                    type="time"
                    label="${t("editLiveEventModal.time")}"
                    .value=${this.scheduledTime}
                    @input=${(e: Event) => {
                      this.scheduledTime = (e.target as HTMLInputElement).value;
                      this.errors = { ...this.errors, time: "", datetime: "" };
                    }}
                    ?error=${!!this.errors.time}
                    help-text=${this.errors.time || (isEnded ? translate("editLiveEventModal.timeCannotChange") : "")}
                    ?disabled=${isEnded}
                    required
                  ></wow-input>
                </div>

                ${this.errors.datetime ? html` <div class="error-message">${this.errors.datetime}</div> ` : nothing}

                <!-- Max Attendees -->
                <wow-input
                  type="number"
                  inputmode="numeric"
                  label="${t("editLiveEventModal.maxAttendees")}"
                  placeholder="${t("editLiveEventModal.maxAttendeesPlaceholder")}"
                  .value=${this.maxAttendees}
                  step="1"
                  max="${MAX_ATTENDEES_LIMIT}"
                  @input=${(e: Event) => {
                    this.maxAttendees = (e.target as HTMLInputElement).value;
                    this.errors = { ...this.errors, maxAttendees: "" };
                  }}
                  ?error=${!!this.errors.maxAttendees}
                ></wow-input>

                <!-- WhatsApp URL -->
                <wow-input
                  type="url"
                  label="${t("editLiveEventModal.whatsApp")}"
                  placeholder="${t("editLiveEventModal.whatsAppPlaceholder")}"
                  .value=${this.whatsappUrl}
                  @input=${(e: Event) => {
                    this.whatsappUrl = (e.target as HTMLInputElement).value;
                  }}
                ></wow-input>

                <!-- Post-Purchase Media -->
                <media-uploader
                  responsive
                  context="cover_image"
                  label="${t("editLiveEventModal.secondThumb")}"
                  viewport="post"
                  currentFileUrl=${this.postBuyingVideoUrl}
                  @change=${this.handleVideoUpload}
                ></media-uploader>

                <!-- CTA Text -->
                <wow-input
                  label="${t("editLiveEventModal.ctaButton")}"
                  placeholder="${t("editLiveEventModal.ctaButtonPlaceholder")}"
                  .value=${this.ctaText}
                  @input=${(e: Event) => {
                    this.ctaText = (e.target as HTMLInputElement).value;
                  }}
                  maxlength="255"
                ></wow-input>

                <!-- Background Images -->
                ${this.hasBackgroundImagesFlag
                  ? html`
                      <div class="background-images-section">
                        <label class="section-label">${t("editLiveEventModal.bgImage")}</label>
                        <div class="background-images-grid">
                          ${[0, 1, 2, 3].map(
                            (index) => html`
                              <media-uploader
                                context="cover_image"
                                viewport="post"
                                currentFileUrl=${this.backgroundImageUrls[index] || ""}
                                @change=${(e: CustomEvent) => this.handleBackgroundImageUpload(index, e)}
                              ></media-uploader>
                            `,
                          )}
                        </div>
                      </div>
                    `
                  : nothing}

                <!-- Stream Test Section (always visible for non-ended events) -->
                ${!isEnded
                  ? html`
                      <div class="stream-test-section">
                        <div class="stream-test-header">${t("editLiveEventModal.streamTestHeader")}</div>
                        <div class="obs-info-section">
                          <div class="obs-info-header">${t("editLiveEventModal.streamFromObs")}</div>
                          <div class="stream-info-row">
                            <span class="stream-info-label">${t("editLiveEventModal.streamUrl")}</span>
                            <code class="stream-info-value">${this.RTMP_URL}</code>
                            <wow-button
                              variant="subtle"
                              size="small"
                              square
                              @click=${() => this.copyToClipboard(this.RTMP_URL, "URL")}
                            >
                              <ph-copy size="16"></ph-copy>
                            </wow-button>
                          </div>
                          <div class="stream-info-row">
                            <span class="stream-info-label">${t("editLiveEventModal.streamKey")}</span>
                            <code class="stream-info-value">${this.eventId}</code>
                            <wow-button
                              variant="subtle"
                              size="small"
                              square
                              @click=${() =>
                                this.copyToClipboard(
                                  this.eventId,
                                  translate("editLiveEventModal.streamKey").replace(":", ""),
                                )}
                            >
                              <ph-copy size="16"></ph-copy>
                            </wow-button>
                          </div>
                        </div>
                        <wow-button variant="primary" size="medium" @click=${this.handleStartStreamingClick}>
                          <ph-broadcast size="18" weight="fill"></ph-broadcast>
                          ${t("editLiveEventModal.startStreaming")}
                        </wow-button>
                      </div>
                    `
                  : nothing}

                <!-- End Event Section (show if event can be ended) -->
                ${canEndEvent
                  ? html`
                      <div class="end-event-section">
                        <div class="warning-banner">
                          <ph-warning size="20" weight="fill"></ph-warning>
                          <span
                            >${isLive
                              ? t("editLiveEventModal.eventLiveWarning")
                              : t("editLiveEventModal.eventPassedWarning")}</span
                          >
                        </div>
                        <wow-button
                          variant="danger"
                          size="medium"
                          @click=${this.handleEndEventClick}
                          ?disabled=${this.isEnding}
                        >
                          <ph-stop size="18" weight="fill"></ph-stop>
                          ${this.isEnding ? t("editLiveEventModal.ending") : t("editLiveEventModal.endEvent")}
                        </wow-button>
                      </div>
                    `
                  : nothing}

                <!-- Stream Statistics - TODO: needs reimplementation for current streaming (was built for SRS) -->
              `}
        </div>

        ${!isEnded && !this.isLoadingEvent
          ? html`<span slot="footer-button-text"
              >${this.isSubmitting ? t("editLiveEventModal.updating") : t("editLiveEventModal.submitButton")}</span
            >`
          : nothing}
      </wow-modal>
      ${this.showEndConfirmation
        ? html`
            <wow-confirmation header="${translate("editLiveEventModal.endEventHeader")}">
              <div class="delete-confirmation-content">
                <p>${t("editLiveEventModal.endEventConfirm")}</p>
                <div class="confirmation-buttons">
                  <wow-button variant="danger" size="medium" @click=${this.handleConfirmEnd}>
                    ${t("editLiveEventModal.endEventButton")}
                  </wow-button>
                  <wow-button variant="neutral" size="medium" @click=${this.handleCancelEnd}>
                    ${t("editLiveEventModal.endEventCancel")}
                  </wow-button>
                </div>
              </div>
            </wow-confirmation>
          `
        : nothing}
      ${this.showStreamConfigModal
        ? html`
            <stream-config-modal
              .streamId=${this.eventId}
              @start-stream=${this.handleStartStream}
              @cancel=${this.handleStreamConfigCancel}
            ></stream-config-modal>
          `
        : nothing}
      ${this.showLiveStreamModal && this.streamConfig
        ? html`
            <live-stream-modal
              .streamId=${this.eventId}
              aspect-ratio=${this.streamConfig.aspectRatio}
              ?enable-audio=${this.streamConfig.enableAudio}
              @stream-ended=${this.handleStreamEnded}
            ></live-stream-modal>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "edit-live-event-modal": EditLiveEventModal;
  }
}
